import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Save, X, Pencil } from "lucide-react";

export const COSTS_UPDATED_EVENT = "order-costs-updated";

export const PARTS_LABEL = { NO_PARTS: "Sin repuesto", WITH_PARTS: "Con repuesto", PENDING: "Repuestos por definir" };

export function partsSummary(it) {
  const p = it.parts_policy;
  if (p === "NO_PARTS") return { text: "Sin repuesto · costo $0", tone: "text-slate-500" };
  if (p === "WITH_PARTS") {
    const note = it.parts_note ? `: ${it.parts_note}` : "";
    const known = it.unit_cost != null || it.cost_set;
    const cost = it.unit_cost != null ? `costo $${Number(it.unit_cost).toFixed(2)}` : it.cost_set ? "costo registrado" : "costo pendiente";
    return { text: `Con repuesto${note} · ${cost}`, tone: known ? "text-slate-500" : "text-amber-600" };
  }
  if (p === "PENDING") return { text: "Repuestos por definir · ganancia pendiente", tone: "text-amber-600" };
  return { text: it.unit_cost != null ? `Costo unit: $${Number(it.unit_cost).toFixed(2)}` : "Sin clasificar · costo no configurado", tone: it.unit_cost != null ? "text-slate-500" : "text-amber-600" };
}

// Admin: completa/corrige clasificación de repuestos y costo real de un ítem.
export const PartsCostEditor = ({ orderId, item, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [policy, setPolicy] = useState(item.parts_policy || "");
  const [cost, setCost] = useState(item.unit_cost != null ? String(item.unit_cost) : "");
  const [note, setNote] = useState(item.parts_note || "");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setPolicy(item.parts_policy || "");
    setCost(item.unit_cost != null ? String(item.unit_cost) : "");
    setNote(item.parts_note || "");
  }, [item.parts_policy, item.unit_cost, item.parts_note]);

  async function save() {
    const c = cost === "" ? null : Number(String(cost).replace(",", "."));
    if (c !== null && (!Number.isFinite(c) || c < 0)) return toast.error("Costo inválido");
    const body = { unit_cost: policy === "NO_PARTS" ? 0 : c };
    if (policy) body.parts_policy = policy;
    if (policy === "WITH_PARTS") body.parts_note = note;
    setSaving(true);
    try {
      await api.patch(`/orders/${orderId}/services/${item.id}/cost`, body);
      toast.success("Costo de repuestos actualizado");
      setEditing(false);
      onSaved?.();
      window.dispatchEvent(new Event(COSTS_UPDATED_EVENT));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  }

  const s = partsSummary(item);
  if (!editing) {
    return (
      <button type="button" onClick={() => setEditing(true)} className={`mt-1 inline-flex items-center gap-1 text-[11px] font-mono hover:underline text-left ${s.tone}`} data-testid={`admin-cost-btn-${item.id}`}>
        {s.text} <Pencil size={10} className="shrink-0" />
      </button>
    );
  }
  return (
    <div className="mt-2 space-y-2 rounded-lg border border-slate-200 bg-slate-50 p-2" data-testid={`admin-cost-edit-${item.id}`}>
      <select value={policy} onChange={(e) => setPolicy(e.target.value)} className="input-base py-1.5 text-xs w-full" data-testid={`admin-parts-policy-${item.id}`}>
        <option value="">Sin clasificar</option>
        {Object.entries(PARTS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
      </select>
      {policy === "WITH_PARTS" && (
        <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={200} placeholder="Repuesto utilizado (opcional)" className="input-base py-1.5 text-xs w-full" data-testid={`admin-parts-note-${item.id}`} />
      )}
      {policy !== "NO_PARTS" && (
        <input type="number" inputMode="decimal" step="0.01" min="0" value={cost} onChange={(e) => setCost(e.target.value)}
          placeholder="Costo real de repuestos (USD)" className="input-base py-1.5 text-xs w-full font-mono" data-testid={`admin-cost-input-${item.id}`} />
      )}
      <div className="flex justify-end gap-1">
        <button type="button" onClick={() => setEditing(false)} className="btn-secondary text-xs py-1"><X size={12} /> Cancelar</button>
        <button type="button" onClick={save} disabled={saving} className="btn-primary text-xs py-1" data-testid={`admin-cost-save-${item.id}`}><Save size={12} /> Guardar</button>
      </div>
    </div>
  );
};
